/**
 * WebSocket connection status types
 */
export type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error'

/**
 * WebSocket message type enum matching backend WebSocketMessageTypes
 * These values must match app/cases/consumers.py WebSocketMessageTypes
 */
export enum WebSocketMessageType {
  CASE_UPDATE = 'case_update',
  CASE_STATUS_CHANGE = 'case_status_change',
  CASE_ACTIVITY = 'case_activity',
  CASE_AGENT_ACTIVITY = 'case_agent_activity',
  CASE_DOCUMENT_PROCESSING = 'case_document_processing',
  CASE_EMAIL_DELIVERY_EVENT = 'case_email_delivery_event',
  CASE_DUPLICATE_DETECTED = 'case_duplicate_detected',
  DOCUMENT_ACTAPORT_SYNC = 'document_actaport_sync',
  DOCUMENT_ACTAPORT_IMPORT = 'document_actaport_import',
  DOCUMENT_ACTAPORT_BULK_SYNC = 'document_actaport_bulk_sync',
  DOCUMENT_STATE_UPDATE = 'document_state_update',
  CONNECTION_ESTABLISHED = 'connection_established',
  MESSAGE_ACKNOWLEDGED = 'message_acknowledged',

  // AI Chat Session types (main app — matches app/ai/consumers.py)
  AI_CHAT_SESSION_STATE_CHANGE = 'session_state_change',
  AI_CHAT_TEXT_DELTA = 'text_delta',
  AI_CHAT_TEXT_CHUNK_END = 'text_chunk_end',
  AI_CHAT_CHUNK = 'chunk',
  AI_CHAT_VERDICT_CONTEXT = 'verdict_context',
  AI_CHAT_MESSAGE_COMPLETE = 'message_complete',
  AI_CHAT_DONE = 'done',
  AI_CHAT_ERROR = 'error',
  AI_CHAT_MODEL_RESOLVED = 'model_resolved',
  AI_CHAT_MODEL_ERROR = 'model_error',

  // Office Add-in AI Chat events (matches app/jupus/office_addin/ consumer)
  OFFICE_ADDIN_CHAT_STATE_UPDATE = 'ai_chat_state_update',
  OFFICE_ADDIN_CHAT_TEXT_DELTA = 'ai_chat_text_delta',
  OFFICE_ADDIN_CHAT_COMPLETE = 'ai_chat_complete',
  OFFICE_ADDIN_CHAT_ERROR = 'ai_chat_error'
}

/**
 * Specific message data types for better type safety
 */
export interface DocumentActaportSyncData {
  document_uuid: string
  status: 'success' | 'failed' | 'syncing'
  error?: string
  case_uuid?: string
  timestamp?: string
}

export interface DocumentActaportImportData {
  actaport_document_id: string
  status: 'success' | 'failed' | 'importing'
  error?: string
  case_uuid?: string
  timestamp?: string
}

export interface DocumentActaportBulkSyncData {
  case_uuid: string
  status: 'started' | 'progress' | 'completed' | 'failed'
  sending_count: number
  importing_count: number
  total_count: number
  completed_count?: number
  error?: string
  timestamp?: string
}

/**
 * AI Chat message data types
 */
export interface AIChatTextDeltaData {
  content: string
  session_uuid?: string
}

export interface AIChatStateUpdateData {
  state: string
  session_uuid?: string
}

export interface AIChatErrorData {
  message: string
  correlation_id?: string
  session_uuid?: string
}

/**
 * WebSocket message format
 */
export interface WebSocketMessage {
  type: WebSocketMessageType
  data: {
    case_uuid?: string
    timestamp?: string
    [key: string]: any
  }
}

/**
 * WebSocket error types
 */
export interface WebSocketError {
  type: 'auth_failed' | 'network_error' | 'rate_limited' | 'permission_denied' | 'unknown'
  message: string
  code?: number
}

/**
 * Channel configuration options
 */
export interface ChannelOptions {
  autoReconnect?: boolean
  maxReconnectAttempts?: number
  reconnectDelay?: number
  /**
   * Authentication token for WebSocket connections.
   * Accepts either a static string or a getter function.
   * A getter function is recommended for long-lived connections
   * (e.g. Office Add-in sidebar) so that a fresh token is used
   * on each reconnect attempt.
   */
  authToken?: string | (() => string)
}

/**
 * Message callback function type
 */
export type MessageCallback = (message: WebSocketMessage) => void

/**
 * Status change callback function type
 */
export type StatusChangeCallback = (status: ConnectionStatus) => void

/**
 * Error callback function type
 */
export type ErrorCallback = (error: WebSocketError) => void
